import type { BlockResponse, ExpertReportListItem } from '@/entities/expert-report';

export type TreeNodeType = 'report' | 'block';

export type TreeNodeData = {
  id: string;
  label: string;
  type: TreeNodeType;
  // Original IDs for navigation
  reportId?: number;
  blockId?: number;
  isHeader?: boolean;
  children?: TreeNodeData[];
};

/**
 * Transforms a block (with children) to tree node recursively
 */
export const blockToTreeNode = (block: BlockResponse, reportId: number): TreeNodeData => ({
  id: `block-${reportId}-${block.id}`,
  label: block.name,
  type: 'block',
  reportId,
  blockId: block.id,
  isHeader: block.isHeader,
  children: block.children
    .filter((child) => !child.isDeleted)
    .sort((a, b) => a.orderPosition - b.orderPosition)
    .map((child) => blockToTreeNode(child, reportId)),
});

/**
 * Transforms a report list item to tree node (without children initially)
 */
export const reportToTreeNode = (report: ExpertReportListItem): TreeNodeData => ({
  id: `report-${report.id}`,
  label: report.name,
  type: 'report',
  reportId: report.id,
  children: undefined,
});

/**
 * Transforms report with loaded blocks to tree node
 */
export const reportWithBlocksToTreeNode = (
  report: ExpertReportListItem,
  blocks: BlockResponse[] | undefined,
): TreeNodeData => ({
  id: `report-${report.id}`,
  label: report.name,
  type: 'report',
  reportId: report.id,
  children: blocks
    ?.filter((block) => !block.isDeleted)
    .sort((a, b) => a.orderPosition - b.orderPosition)
    .map((block) => blockToTreeNode(block, report.id)),
});

/**
 * Parse tree node ID to extract type and IDs
 */
export const parseTreeNodeId = (nodeId: string): { type: TreeNodeType; reportId?: number; blockId?: number } => {
  const parts = nodeId.split('-');
  const type = parts[0] as TreeNodeType;

  const safeParseInt = (value: string | undefined): number | undefined => {
    if (value === undefined) return undefined;
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  };

  if (type === 'report') {
    return { type, reportId: safeParseInt(parts[1]) };
  }

  if (type === 'block') {
    return { type, reportId: safeParseInt(parts[1]), blockId: safeParseInt(parts[2]) };
  }

  return { type };
};

/**
 * Check whether a target node exists anywhere in the subtree.
 */
export const hasDescendant = (nodes: TreeNodeData[], targetId: string): boolean => {
  for (const node of nodes) {
    if (node.id === targetId) return true;
    if (node.children && hasDescendant(node.children, targetId)) return true;
  }
  return false;
};

/**
 * Extract a block by ID from the tree, returning the modified tree and the extracted block.
 */
const extractBlockFromTree = (
  blocks: BlockResponse[],
  blockId: number,
  extracted: { block: BlockResponse | null },
): BlockResponse[] => {
  const result: BlockResponse[] = [];
  for (const block of blocks) {
    if (block.id === blockId) {
      extracted.block = block;
    } else {
      result.push({
        ...block,
        children: extractBlockFromTree(block.children ?? [], blockId, extracted),
      });
    }
  }
  return result;
};

/**
 * Insert a block into the tree under a given parent at a given index.
 */
const insertBlockIntoTree = (
  blocks: BlockResponse[],
  blockToInsert: BlockResponse,
  parentId: number | null,
  index: number,
): BlockResponse[] => {
  if (parentId === null) {
    // Insert at root level
    const result = [...blocks];
    result.splice(index, 0, blockToInsert);
    return result.map((b, i) => ({ ...b, orderPosition: i }));
  }
  return blocks.map((block) => {
    if (block.id === parentId) {
      const newChildren = [...(block.children ?? [])];
      newChildren.splice(index, 0, blockToInsert);
      return {
        ...block,
        children: newChildren.map((c, i) => ({ ...c, orderPosition: i })),
      };
    }
    return {
      ...block,
      children: insertBlockIntoTree(block.children ?? [], blockToInsert, parentId, index),
    };
  });
};

/**
 * Optimistically move a block to a new parent in the BlockResponse tree.
 * Re-normalizes orderPosition for affected containers.
 */
export const moveBlockInTree = (
  blocks: BlockResponse[],
  blockId: number,
  newParentBlockId: number | null,
  newIndex: number,
): BlockResponse[] => {
  const extracted: { block: BlockResponse | null } = { block: null };
  const withoutMoved = extractBlockFromTree(blocks, blockId, extracted);
  if (!extracted.block) return blocks;
  return insertBlockIntoTree(withoutMoved, extracted.block, newParentBlockId, newIndex);
};

/**
 * Извлекает числовой ID блока из nodeId вида "block-{reportId}-{blockId}".
 * Для не-блочных nodeId возвращает null.
 */
export const parseBlockNodeId = (nodeId: string): number | null => {
  const parts = nodeId.split('-');
  if (parts[0] !== 'block') return null;
  const blockId = Number(parts[2]);
  return Number.isFinite(blockId) ? blockId : null;
};

/** Зона строки-цели под указателем: `before`/`after` — вставка рядом, `nest` — вложение под лист. */
export type DropBand = 'before' | 'nest' | 'after';

/**
 * Определяет зону вставки по вертикальному положению указателя внутри строки-цели.
 *
 * Для листа (`allowNest`) строка делится на три части: верх — вставка до, середина — вложение
 * под лист, низ — вставка после (в т.ч. «встать последним на этом уровне»). Так позиция «в конец
 * уровня» больше не перехватывается вложением и обозначается линией, а вложение — контуром.
 * Для остальных целей — только `before`/`after` по центру, без вложения по вертикали.
 *
 * Вертикальный признак выбран намеренно: ручка перетаскивания находится у правого края строки,
 * поэтому указатель во время drag «прилипает» к правому краю и горизонтальный отступ ненадёжен.
 */
export const computeDropBand = (
  pointerY: number,
  rectTop: number,
  rectHeight: number,
  allowNest: boolean,
): DropBand => {
  if (!allowNest || rectHeight <= 0) {
    return pointerY > rectTop + rectHeight / 2 ? 'after' : 'before';
  }
  const ratio = (pointerY - rectTop) / rectHeight;
  if (ratio < 1 / 3) return 'before';
  if (ratio > 2 / 3) return 'after';
  return 'nest';
};

/**
 * Строит сгруппированное представление для хелпера `move` из @dnd-kit/helpers:
 * ключ — ID группы сортировки (родительский контейнер), значение — упорядоченный
 * список nodeId дочерних блоков. Удалённые блоки исключаются — они не сортируются в UI,
 * поэтому их nodeId не должны попадать в группы сортировки.
 */
export const buildSortableGroups = (blocks: BlockResponse[], reportId: number): Record<string, string[]> => {
  const groups: Record<string, string[]> = {};
  const walk = (nodes: BlockResponse[], groupId: string) => {
    const active = nodes.filter((b) => !b.isDeleted).sort((a, b) => a.orderPosition - b.orderPosition);
    groups[groupId] = active.map((b) => `block-${reportId}-${b.id}`);
    for (const block of active) {
      if (block.children?.length) {
        walk(block.children, `block-${reportId}-${block.id}`);
      }
    }
  };
  walk(blocks, `report-${reportId}`);
  return groups;
};

/**
 * Пересобирает дерево блоков из сгруппированного представления (результата `move`).
 * Расставляет родителей и порядок активных блоков, заново нумерует orderPosition и
 * сохраняет удалённые блоки (вместе с их исходным поддеревом) у исходного родителя —
 * они не участвуют в сортировке, поэтому сгруппированное представление их не описывает.
 */
export const rebuildBlocksFromGroups = (
  blocks: BlockResponse[],
  groups: Record<string, string[]>,
  reportId: number,
): BlockResponse[] => {
  const byId = new Map<number, BlockResponse>();
  // Удалённые блоки, сгруппированные по исходному родителю (null — корневой уровень)
  const deletedByParent = new Map<number | null, BlockResponse[]>();

  const collect = (nodes: BlockResponse[], parentBlockId: number | null) => {
    for (const block of nodes) {
      if (block.isDeleted) {
        const siblings = deletedByParent.get(parentBlockId) ?? [];
        siblings.push(block); // сохраняем всё исходное поддерево без изменений
        deletedByParent.set(parentBlockId, siblings);
        continue; // не спускаемся внутрь удалённого поддерева
      }
      byId.set(block.id, { ...block, children: [] });
      collect(block.children ?? [], block.id);
    }
  };
  collect(blocks, null);

  const buildChildren = (parentBlockId: number | null): BlockResponse[] => {
    const groupId = parentBlockId === null ? `report-${reportId}` : `block-${reportId}-${parentBlockId}`;
    const active: BlockResponse[] = [];
    (groups[groupId] ?? []).forEach((nodeId, index) => {
      const blockId = parseBlockNodeId(nodeId);
      if (blockId === null) return;
      const block = byId.get(blockId);
      if (!block) return;
      block.orderPosition = index;
      active.push(block);
    });
    const deleted = deletedByParent.get(parentBlockId) ?? [];
    return [...active, ...deleted];
  };

  // Проставляем детей каждому активному блоку (лист получает только сохранённых удалённых детей)
  for (const block of byId.values()) {
    block.children = buildChildren(block.id);
  }
  return buildChildren(null);
};

/**
 * Стабильная сигнатура порядка и вложенности активных блоков — для проверки, изменил ли
 * drag фактическую раскладку (чтобы не сохранять холостую перестановку).
 */
export const getBlocksOrderSignature = (blocks: BlockResponse[]): string => {
  const parts: string[] = [];
  const walk = (nodes: BlockResponse[], parentBlockId: number | null) => {
    nodes
      .filter((b) => !b.isDeleted)
      .sort((a, b) => a.orderPosition - b.orderPosition)
      .forEach((block, index) => {
        parts.push(`${parentBlockId}:${block.id}:${index}`);
        if (block.children?.length) walk(block.children, block.id);
      });
  };
  walk(blocks, null);
  return parts.join('|');
};

/**
 * Собирает ID всех потомков блока (без самого блока) — чтобы запретить перемещение блока
 * внутрь собственного поддерева (это создало бы цикл).
 */
export const collectSubtreeBlockIds = (blocks: BlockResponse[], rootBlockId: number): Set<number> => {
  const result = new Set<number>();
  const gather = (nodes: BlockResponse[]) => {
    for (const block of nodes) {
      result.add(block.id);
      if (block.children?.length) gather(block.children);
    }
  };
  const findRoot = (nodes: BlockResponse[]): boolean => {
    for (const block of nodes) {
      if (block.id === rootBlockId) {
        gather(block.children ?? []);
        return true;
      }
      if (block.children?.length && findRoot(block.children)) return true;
    }
    return false;
  };
  findRoot(blocks);
  return result;
};
